/**
 * Sales Plan 15-Month Mock Data
 *
 * This file contains mock data for the extended 15-month Sales Plan view.
 * Months 1-12 represent the standard planning period.
 * Months 13-15 represent the EXTENDED FORECAST period (marked with isExtended flag).
 */

import {
  singleConceptInterface,
  singleContractInterface,
  notesInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';

// ---------------------------------------------------------------------------
// 15-month helper
// ---------------------------------------------------------------------------

export interface PlanningMonth {
  label: string;
  dateString: string;
  /** true for months 13-15 (the 3 additional extended forecast months) */
  isExtended: boolean;
}

export const getFutureMonths15 = (): PlanningMonth[] => {
  const months: PlanningMonth[] = [];
  const now = new Date();
  // Start from the 1st of next month
  const year = now.getFullYear();
  let month = now.getMonth() + 1; // 0-indexed → next month

  for (let i = 0; i < 15; i++) {
    const m = month % 12 || 12;
    const y = year + Math.floor((month - 1) / 12);
    const dateString = `${y}-${String(m).padStart(2, '0')}-01`;
    const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    months.push({ label, dateString, isExtended: i >= 12 });
    month++;
  }

  return months;
};

// Pre-compute the 15 months so every array below uses the same set
const PLANNING_MONTHS = getFutureMonths15();

// ---------------------------------------------------------------------------
// Concept details (kept for SalesPlanEdit backward-compat)
// ---------------------------------------------------------------------------

export const mockSalesPlanConceptDetails = {
  gfc: 54321,
  Strength: '20mg',
  DrugName: 'Lisinopril',
  DosageForm: 'Tablet',
  ContractNumber: 'SPE2D1-25-D-0098',
  AwardDate: '2024-10-01',
  ContractExpiryDate: '2029-09-30',
};

// ---------------------------------------------------------------------------
// Concept details — matches singleConceptInterface (used by ConceptDetailsSection)
// ---------------------------------------------------------------------------

export const mockConceptDetails: singleConceptInterface = {
  concept: 'Lisinopril 20mg Tablet',
  gfc: 54321,
  generic_rxcui: '29046',
  therapeutic_category: 'Cardiovascular',
  pharmacologic_class: 'ACE Inhibitors',
  va_class_name: 'CV800',
  des_schedule: 'N/A',
  duration_condition: 'Chronic',
  Strength: '20mg',
};

// ---------------------------------------------------------------------------
// Contract details — matches singleContractInterface[] (used by ConceptDetailsSection)
// ---------------------------------------------------------------------------

export const mockContractDetails: singleContractInterface[] = [
  {
    GFC: 54321,
    ContractNumber: 'SPE2D1-25-D-0098',
    ContractType: 'VA FSS',
    StartDate: '2024-10-01',
    EndDate: '2029-09-30',
  },
];

// ---------------------------------------------------------------------------
// Notes mock data — matches notesInterface[] (used by NotesSection)
// ---------------------------------------------------------------------------

export const mockNotesData: notesInterface[] = [
  {
    ID: 1,
    Notes: 'Market share plan reviewed and approved for Q4 2025.',
    CreatedBy: 'John Smith',
    CreatedDate: '2025-10-15T10:30:00Z',
  },
  {
    ID: 2,
    Notes: 'Extended forecast months (13-15) reflect seasonal demand adjustment based on historical VA utilisation patterns.',
    CreatedBy: 'Jane Doe',
    CreatedDate: '2025-11-01T14:45:00Z',
  },
];

// ---------------------------------------------------------------------------
// COT / category constants
// ---------------------------------------------------------------------------

const COT_TYPES = ['cmop', 'va facility', 'tmop', 'mtf'] as const;
type CotType = (typeof COT_TYPES)[number];

const CATEGORIES = ['direct', 'indirect'] as const;
type Category = (typeof CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Sales Summary
// ---------------------------------------------------------------------------

export interface SalesSummaryData {
  COT: string;
  YearToDateDirectShare: number | null;
  YearToDateIndirectShare: number | null;
  LastThreeMonthDirectShare: number | null;
  LastThreeMonthIndirectShare: number | null;
  LastMonthDirectShare: number | null;
  LastMonthIndirectShare: number | null;
}

export const mockSalesSummary: SalesSummaryData[] = [
  {
    COT: 'cmop',
    YearToDateDirectShare: 45.2,
    YearToDateIndirectShare: 12.8,
    LastThreeMonthDirectShare: 47.1,
    LastThreeMonthIndirectShare: 13.5,
    LastMonthDirectShare: 48.3,
    LastMonthIndirectShare: 14.1,
  },
  {
    COT: 'va facility',
    YearToDateDirectShare: 32.6,
    YearToDateIndirectShare: 8.4,
    LastThreeMonthDirectShare: 33.9,
    LastThreeMonthIndirectShare: 9.1,
    LastMonthDirectShare: 34.5,
    LastMonthIndirectShare: 9.7,
  },
  {
    COT: 'tmop',
    YearToDateDirectShare: 18.7,
    YearToDateIndirectShare: 5.3,
    LastThreeMonthDirectShare: 19.4,
    LastThreeMonthIndirectShare: 5.8,
    LastMonthDirectShare: 20.1,
    LastMonthIndirectShare: 6.2,
  },
  {
    COT: 'mtf',
    YearToDateDirectShare: 22.9,
    YearToDateIndirectShare: 7.1,
    LastThreeMonthDirectShare: 23.6,
    LastThreeMonthIndirectShare: 7.5,
    LastMonthDirectShare: 24.2,
    LastMonthIndirectShare: 8.0,
  },
];

// ---------------------------------------------------------------------------
// Realistic value generators (deterministic, seeded by index)
// ---------------------------------------------------------------------------

/**
 * Base parameters per COT + category used to generate realistic numbers.
 * direct channels tend to have higher share / volumes than indirect.
 */
interface BaseParams {
  stratCode: string;
  baseTms: number;
  baseMsPlan: number;
  baseMarketForecast: number;
  adjFactor: number; // multiplier applied to market forecast
}

const BASE: Record<CotType, Record<Category, BaseParams>> = {
  cmop: {
    direct: { stratCode: 'Annual Plan', baseTms: 45, baseMsPlan: 46, baseMarketForecast: 270000, adjFactor: 0.97 },
    indirect: { stratCode: 'Annual Plan', baseTms: 13, baseMsPlan: 13, baseMarketForecast: 290000, adjFactor: 0.95 },
  },
  'va facility': {
    direct: { stratCode: 'Annual Plan', baseTms: 33, baseMsPlan: 33, baseMarketForecast: 295000, adjFactor: 0.98 },
    indirect: { stratCode: 'Annual Plan', baseTms: 9, baseMsPlan: 9, baseMarketForecast: 305000, adjFactor: 0.94 },
  },
  tmop: {
    direct: { stratCode: 'Annual Plan', baseTms: 19, baseMsPlan: 19, baseMarketForecast: 320000, adjFactor: 0.96 },
    indirect: { stratCode: 'Annual Plan', baseTms: 6, baseMsPlan: 6, baseMarketForecast: 335000, adjFactor: 0.93 },
  },
  mtf: {
    direct: { stratCode: 'Annual Plan', baseTms: 23, baseMsPlan: 23, baseMarketForecast: 340000, adjFactor: 0.97 },
    indirect: { stratCode: 'Annual Plan', baseTms: 7, baseMsPlan: 7, baseMarketForecast: 350000, adjFactor: 0.94 },
  },
};

/** Simple deterministic pseudo-random jitter so each month is slightly different */
const jitter = (base: number, monthIdx: number, seed: number): number => {
  const noise = Math.sin(monthIdx * 13.37 + seed * 7.91) * 0.05; // ±5 %
  return Math.round(base * (1 + noise));
};

const pctJitter = (base: number, monthIdx: number, seed: number): number => {
  const noise = Math.sin(monthIdx * 11.23 + seed * 5.67) * 2; // ±2 pp
  return Math.round(base + noise);
};

// ---------------------------------------------------------------------------
// MS Plan View data (read-only table)
// ---------------------------------------------------------------------------

export interface MSPlanViewRow {
  planningPeriod: string;
  planningPeriodLabel: string;
  isExtended: boolean;
  cot: string;
  category: 'direct' | 'indirect';
  stratCode: string;
  tmsValue: string;
  msPlan: string;
}

export const mockMSPlanViewData: MSPlanViewRow[] = (() => {
  const rows: MSPlanViewRow[] = [];
  let seed = 0;

  for (const pm of PLANNING_MONTHS) {
    for (const cot of COT_TYPES) {
      for (const cat of CATEGORIES) {
        const bp = BASE[cot][cat];
        const tms = jitter(bp.baseTms, PLANNING_MONTHS.indexOf(pm), seed++);
        const ms = pctJitter(bp.baseMsPlan, PLANNING_MONTHS.indexOf(pm), seed);
        rows.push({
          planningPeriod: pm.dateString,
          planningPeriodLabel: pm.label,
          isExtended: pm.isExtended,
          cot,
          category: cat,
          stratCode: bp.stratCode,
          tmsValue: `${tms}%`,
          msPlan: `${ms}%`,
        });
      }
    }
  }

  return rows;
})();

// ---------------------------------------------------------------------------
// Sales Plan View data (read-only table)
// ---------------------------------------------------------------------------

export interface SalesPlanViewRow {
  planningPeriod: string;
  planningPeriodLabel: string;
  isExtended: boolean;
  cot: string;
  category: 'direct' | 'indirect';
  marketForecast: number;
  adjMarketForecast: number;
  tmsValue: string;
  msPlan: string;
  euSalesPlan: number;
}

export const mockSalesPlanViewData: SalesPlanViewRow[] = (() => {
  const rows: SalesPlanViewRow[] = [];
  let seed = 100;

  for (const pm of PLANNING_MONTHS) {
    const mi = PLANNING_MONTHS.indexOf(pm);
    for (const cot of COT_TYPES) {
      for (const cat of CATEGORIES) {
        const bp = BASE[cot][cat];
        const mf = jitter(bp.baseMarketForecast, mi, seed++);
        const amf = Math.round(mf * bp.adjFactor);
        const tms = jitter(bp.baseTms, mi, seed);
        const ms = pctJitter(bp.baseMsPlan, mi, seed);
        const euSales = Math.round(amf * (ms / 100));
        rows.push({
          planningPeriod: pm.dateString,
          planningPeriodLabel: pm.label,
          isExtended: pm.isExtended,
          cot,
          category: cat,
          marketForecast: mf,
          adjMarketForecast: amf,
          tmsValue: `${tms}%`,
          msPlan: `${ms}%`,
          euSalesPlan: euSales,
        });
      }
    }
  }

  return rows;
})();

// ---------------------------------------------------------------------------
// MS Plan Edit data (editable msPlan percentages)
// ---------------------------------------------------------------------------

export interface MSPlanEditRow {
  planningPeriod: string;
  planningPeriodLabel: string;
  isExtended: boolean;
  cot: string;
  category: 'direct' | 'indirect';
  stratCode: string;
  tmsValue: string;
  /** Editable percentage value (numeric whole number, e.g. 46) */
  msPlan: number;
}

export const mockMSPlanEditData: MSPlanEditRow[] = (() => {
  const rows: MSPlanEditRow[] = [];
  let seed = 200;

  for (const pm of PLANNING_MONTHS) {
    const mi = PLANNING_MONTHS.indexOf(pm);
    for (const cot of COT_TYPES) {
      for (const cat of CATEGORIES) {
        const bp = BASE[cot][cat];
        const tms = jitter(bp.baseTms, mi, seed++);
        const ms = pctJitter(bp.baseMsPlan, mi, seed);
        rows.push({
          planningPeriod: pm.dateString,
          planningPeriodLabel: pm.label,
          isExtended: pm.isExtended,
          cot,
          category: cat,
          stratCode: bp.stratCode,
          tmsValue: `${tms}%`,
          msPlan: ms,
        });
      }
    }
  }

  return rows;
})();

// ---------------------------------------------------------------------------
// Sales Plan Edit data (editable adjMarketForecast)
// ---------------------------------------------------------------------------

export interface SalesPlanEditRow {
  planningPeriod: string;
  planningPeriodLabel: string;
  isExtended: boolean;
  cot: string;
  category: 'direct' | 'indirect';
  marketForecast: number;
  /** Editable */
  adjMarketForecast: number;
  tmsValue: string;
  msPlan: string;
  euSalesPlan: number;
  overallMSPercent: number;
}

export const mockSalesPlanEditData: SalesPlanEditRow[] = (() => {
  const rows: SalesPlanEditRow[] = [];
  let seed = 300;

  for (const pm of PLANNING_MONTHS) {
    const mi = PLANNING_MONTHS.indexOf(pm);
    for (const cot of COT_TYPES) {
      for (const cat of CATEGORIES) {
        const bp = BASE[cot][cat];
        const mf = jitter(bp.baseMarketForecast, mi, seed++);
        const amf = Math.round(mf * bp.adjFactor);
        const tms = jitter(bp.baseTms, mi, seed);
        const ms = pctJitter(bp.baseMsPlan, mi, seed);
        const euSales = Math.round(amf * (ms / 100));
        // overallMSPercent is a blended share across direct+indirect for this COT
        const overallMs = Math.round(ms * 0.85 + pctJitter(bp.baseMsPlan * 0.4, mi, seed + 1) * 0.15);
        rows.push({
          planningPeriod: pm.dateString,
          planningPeriodLabel: pm.label,
          isExtended: pm.isExtended,
          cot,
          category: cat,
          marketForecast: mf,
          adjMarketForecast: amf,
          tmsValue: `${tms}%`,
          msPlan: `${ms}%`,
          euSalesPlan: euSales,
          overallMSPercent: overallMs,
        });
      }
    }
  }

  return rows;
})();

// ---------------------------------------------------------------------------
// Helper: filter rows by standard (months 1-12) vs extended (months 13-15)
// ---------------------------------------------------------------------------

export const getStandardMonthRows = <T extends { isExtended: boolean }>(rows: T[]): T[] =>
  rows.filter((r) => !r.isExtended);

export const getExtendedMonthRows = <T extends { isExtended: boolean }>(rows: T[]): T[] =>
  rows.filter((r) => r.isExtended);

// ---------------------------------------------------------------------------
// Note about extended months
// ---------------------------------------------------------------------------

/**
 * UI hint: months 13-15 carry `isExtended: true`.
 * Consuming components should render these with a distinct visual treatment
 * (e.g., a shaded column background or a divider line after month 12)
 * and display the EXTENDED_FORECAST_LABEL in the column header group.
 */
export const EXTENDED_FORECAST_LABEL = 'Extended Forecast';
export const STANDARD_PERIOD_MONTH_COUNT = 12;
export const EXTENDED_PERIOD_MONTH_COUNT = 3;
export const TOTAL_PLANNING_MONTHS = 15;
